import { useState, useEffect, useCallback, useMemo } from 'react';
import { db, functions } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import Card from './ui/Card';
import Button from './ui/Button';
import Modal from './ui/Modal';
import { toast } from './ui/Toast';
import { useTranslation } from '../i18n/LanguageContext';
import {
  IconBuildingWarehouse,
  IconCheck,
  IconX,
  IconSearch,
  IconAlertTriangle,
} from '@tabler/icons-react';

const fmt = (ts, locale) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function WorkshopLinks() {
  const { language } = useTranslation();
  const { orgId, role } = useAuth();
  const isAdmin = role === 'org_admin';

  const [links, setLinks] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Approval dialog
  const [approving, setApproving] = useState(null);
  const [scope, setScope] = useState('fleet');
  const [chosen, setChosen] = useState([]);
  const [search, setSearch] = useState('');

  // Revoke confirmation
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const [linkSnap, machineSnap] = await Promise.all([
        getDocs(query(collection(db, 'organisations', orgId, 'linkedWorkshops'), orderBy('requestedAt', 'desc'))),
        getDocs(collection(db, 'organisations', orgId, 'machines')),
      ]);
      setLinks(linkSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setMachines(machineSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const pending = links.filter(l => l.status === 'pending');
  const active = links.filter(l => l.status === 'active');

  const machineLabel = (m) =>
    [m.makeLabel || m.make, m.modelLabel || m.model].filter(Boolean).join(' ') || m.registration || m.id;

  const filteredMachines = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return machines;
    return machines.filter(m =>
      machineLabel(m).toLowerCase().includes(q) ||
      String(m.registration || '').toLowerCase().includes(q) ||
      String(m.serialNumber || '').toLowerCase().includes(q)
    );
  }, [machines, search]);

  const respond = async (link, approve) => {
    setBusy(link.id);
    try {
      const fn = httpsCallable(functions, 'respondToWorkshopLink');
      await fn({
        orgId,
        linkId: link.id,
        approve,
        scope: approve ? scope : undefined,
        machineIds: approve && scope === 'machines' ? chosen : [],
      });
      setApproving(null);
      setChosen([]);
      setScope('fleet');
      setSearch('');
      await load();
      toast.success(approve ? `${link.workshopName} connected` : 'Request declined');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not update this request');
    }
    setBusy(null);
  };

  const revoke = async (link) => {
    setBusy(link.id);
    try {
      const fn = httpsCallable(functions, 'revokeWorkshopLink');
      const res = await fn({ orgId, linkId: link.id });
      setRevoking(null);
      await load();
      toast.success(`Disconnected — ${res?.data?.jobCount ?? 0} jobs kept on your machines`);
    } catch (err) {
      console.error(err);
      // The callable refuses while work is unsealed; say which jobs.
      const open = err?.details?.openJobs;
      toast.error(open?.length ? `Still open: ${open.join(', ')}` : (err?.message || 'Could not disconnect'));
    }
    setBusy(null);
  };

  if (loading || (pending.length === 0 && active.length === 0)) return null;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--color-text-tertiary)', marginBottom: 8 }}>
        Connected workshops
      </div>

      {pending.map(link => (
        <Card key={link.id} style={{ marginBottom: 12, borderLeft: '2px solid #FF6A00' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-lg)', background: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconBuildingWarehouse size={20} color="var(--color-text-secondary)" stroke={1.5} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, marginBottom: 4 }}>{link.workshopName}</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', lineHeight: 1.6, marginBottom: 10 }}>
                Has asked to connect using your organisation code. Approving lets them see
                the machines you choose and record the work they do on them — that work then
                appears on each machine's record and its passport.
              </div>
              {isAdmin ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" icon={<IconCheck size={13} stroke={1.5} />}
                          onClick={() => { setApproving(link); setScope('fleet'); setChosen([]); }}>
                    Review and approve
                  </Button>
                  <Button size="sm" variant="secondary" icon={<IconX size={13} stroke={1.5} />}
                          disabled={busy === link.id} loading={busy === link.id}
                          onClick={() => respond(link, false)}>
                    Decline
                  </Button>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                  An organisation admin needs to approve this.
                </div>
              )}
            </div>
          </div>
        </Card>
      ))}

      {active.map(link => (
        <Card key={link.id} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-lg)', background: 'var(--color-success-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconBuildingWarehouse size={20} color="var(--color-success)" stroke={1.5} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 14 }}>{link.workshopName}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--color-success)', background: 'var(--color-success-light)', padding: '2px 8px', borderRadius: 20 }}>
                  <IconCheck size={11} stroke={2} /> Connected
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
                {link.scope === 'machines'
                  ? `${(link.machineIds || []).length} machine${(link.machineIds || []).length === 1 ? '' : 's'} shared`
                  : 'Whole fleet shared'}
                {' · approved '}{fmt(link.approvedAt, language)}
                {link.approvedBy ? ` by ${link.approvedBy}` : ''}
              </div>
              {isAdmin && (
                <Button size="sm" variant="danger-outline"
                        disabled={busy === link.id} loading={busy === link.id}
                        onClick={() => setRevoking(link)}>
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}

      {/* Approval — scope is chosen at the moment of consent, not afterwards. */}
      <Modal
        open={!!approving}
        onClose={() => setApproving(null)}
        title={approving ? `Connect ${approving.workshopName}` : ''}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setApproving(null)}>Cancel</Button>
            <Button
              onClick={() => respond(approving, true)}
              disabled={busy === approving?.id || (scope === 'machines' && chosen.length === 0)}
              loading={busy === approving?.id}
              icon={<IconCheck size={14} stroke={1.5} />}
            >
              {scope === 'machines' ? `Connect for ${chosen.length} machine${chosen.length === 1 ? '' : 's'}` : 'Connect for whole fleet'}
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 16 }}>
          They will be able to see these machines and record work against them. They cannot
          see your staff, your checks, or anything else in your account. You can disconnect
          at any time, and work already recorded stays on your machines.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {[
            { key: 'fleet', label: 'My whole fleet', hint: `All ${machines.length} machines, including any added later` },
            { key: 'machines', label: 'Only certain machines', hint: 'Choose which ones below' },
          ].map(opt => (
            <div
              key={opt.key}
              onClick={() => setScope(opt.key)}
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
                padding: '10px 12px', borderRadius: 'var(--radius-md)',
                border: `0.5px solid ${scope === opt.key ? 'var(--color-brand)' : 'var(--color-border-medium)'}`,
                background: scope === opt.key ? 'rgba(232,97,10,0.05)' : 'transparent',
              }}
            >
              <div style={{
                width: 15, height: 15, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                border: `1.5px solid ${scope === opt.key ? 'var(--color-brand)' : 'var(--color-border-medium)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {scope === opt.key && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--color-brand)' }} />}
              </div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{opt.hint}</div>
              </div>
            </div>
          ))}
        </div>

        {scope === 'machines' && (
          <div>
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <IconSearch size={14} stroke={1.5} style={{ position: 'absolute', left: 10, top: 9, color: 'var(--color-text-tertiary)' }} />
              <input
                placeholder="Search machines"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 30px', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-md)' }}>
              {filteredMachines.map(m => {
                const on = chosen.includes(m.id);
                return (
                  <div
                    key={m.id}
                    onClick={() => setChosen(prev => on ? prev.filter(x => x !== m.id) : [...prev, m.id])}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer',
                      borderBottom: '0.5px solid var(--color-border-light)',
                      background: on ? 'rgba(232,97,10,0.05)' : 'transparent',
                    }}
                  >
                    <div style={{
                      width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                      border: `1.5px solid ${on ? 'var(--color-brand)' : 'var(--color-border-medium)'}`,
                      background: on ? 'var(--color-brand)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {on && <IconCheck size={10} color="#fff" stroke={3} />}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{machineLabel(m)}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                        {m.registration || m.serialNumber || '—'}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredMachines.length === 0 && (
                <div style={{ padding: '12px', fontSize: 12, color: 'var(--color-text-tertiary)' }}>No machines match.</div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Disconnect */}
      <Modal
        open={!!revoking}
        onClose={() => setRevoking(null)}
        title={revoking ? `Disconnect ${revoking.workshopName}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevoking(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => revoke(revoking)}
                    disabled={busy === revoking?.id} loading={busy === revoking?.id}>
              Disconnect
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 12 }}>
          <IconAlertTriangle size={15} color="var(--color-warning)" stroke={1.5} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
            They will no longer see your machines or be able to record work. Any job cards
            still open with them must be sealed first.
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
          Work they have already recorded stays on your machines and on their passports —
          disconnecting does not remove history.
        </div>
      </Modal>
    </div>
  );
}
